import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppStateService, Toast } from './services/app-state.service';
import { DataLayerService } from './services/datalayer.service';
import { JourneyContext, INITIAL_CONTEXT } from './models/wallpaper.model';
import { captureGclidFromUrl } from './utils/ga-attribution.util';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  toasts: Toast[] = [];
  showPromoBanner = true;
  userType: 'new' | 'existing' = 'new';
  context: JourneyContext = { ...INITIAL_CONTEXT };
  currentRoute = '/';

  // Draggable panel state
  panelPos = { x: 0, y: 80 };
  private dragging = false;
  private dragOffset = { x: 0, y: 0 };

  private sub = new Subscription();

  constructor(public state: AppStateService, public router: Router, private dataLayer: DataLayerService) {}

  ngOnInit() {
    captureGclidFromUrl();
    // Store landing page URL once per session (used by WhatsApp click tracking)
    if (!sessionStorage.getItem('landing_page')) {
      sessionStorage.setItem('landing_page', window.location.href);
    }
    this.panelPos = { x: window.innerWidth - 260, y: 80 };
    this.sub.add(this.state.toasts$.subscribe(t => this.toasts = t));
    this.sub.add(this.state.showPromoBanner$.subscribe(v => this.showPromoBanner = v));
    this.sub.add(this.state.userType$.subscribe(t => this.userType = t));
    this.sub.add(this.state.context$.subscribe(c => this.context = c));
    this.dataLayer.init();
    this.sub.add(
      this.router.events.pipe(filter((e: any) => e instanceof NavigationEnd)).subscribe((e: any) => {
        this.currentRoute = e.url;
      })
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  dismissToast(id: number) {
    this.state.dismissToast(id);
  }

  dismissPromoBanner() {
    this.state.showPromoBanner$.next(false);
  }

  setUserType(type: 'new' | 'existing') {
    this.state.setUserType(type);
    this.router.navigate(['/']);
  }

  get contextSummary(): string[] {
    return [
      this.context.selected_space ? `Space: ${this.context.selected_space}` : '',
      this.context.selected_category ? `Style: ${this.context.selected_category}` : '',
      this.context.selected_mood ? `Mood: ${this.context.selected_mood}` : '',
      this.context.interaction_history.length > 0 ? `${this.context.interaction_history.length} viewed` : '',
    ].filter(Boolean);
  }

  get currentPageLabel(): string {
    if (this.currentRoute === '/' || this.currentRoute === '') return 'home';
    if (this.currentRoute.startsWith('/listing')) return 'listing';
    if (this.currentRoute.startsWith('/pdp')) return 'pdp';
    if (this.currentRoute.startsWith('/cart')) return 'cart';
    if (this.currentRoute.startsWith('/wishlist')) return 'wishlist';
    if (this.currentRoute.startsWith('/custom-upload')) return 'custom-upload';
    if (this.currentRoute.startsWith('/custom-design')) return 'custom-design';
    return this.currentRoute.replace('/', '');
  }

  onPanelPointerDown(e: PointerEvent) {
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle]')) return;
    this.dragging = true;
    this.dragOffset = { x: e.clientX - this.panelPos.x, y: e.clientY - this.panelPos.y };
    target.setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  onPanelPointerMove(e: PointerEvent) {
    if (!this.dragging) return;
    const newX = Math.max(0, Math.min(window.innerWidth - 250, e.clientX - this.dragOffset.x));
    const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - this.dragOffset.y));
    this.panelPos = { x: newX, y: newY };
  }

  onPanelPointerUp() {
    this.dragging = false;
  }

  getToastBorderClass(toast: Toast): string {
    if (toast.type === 'cart') return 'bg-white border-green-200';
    if (toast.type === 'wishlist') return 'bg-white border-rose-200';
    return 'bg-white border-gray-200';
  }

  getToastIconBgClass(toast: Toast): string {
    if (toast.type === 'cart') return 'bg-green-100';
    if (toast.type === 'wishlist') return 'bg-rose-100';
    if (toast.type === 'wishlist-remove') return 'bg-gray-100';
    return 'bg-blue-100';
  }
}
